import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  {
    id: "01",
    title: "Little Stars",
    age_group: "U8",
    schedule: "Mon & Wed · 16:00 – 17:00",
    description:
      "Foundational ball control, motor skills and a first taste of teamwork through games.",
  },
  {
    id: "02",
    title: "Rising Stars",
    age_group: "U10 – U12",
    schedule: "Tue & Thu · 16:00 – 17:30",
    description:
      "Technical fundamentals, passing patterns and small-sided match play.",
  },
  {
    id: "03",
    title: "Elite Development",
    age_group: "U14 – U16",
    schedule: "Mon, Wed, Fri · 16:30 – 18:30",
    description:
      "Position-specific coaching, tactical awareness and competitive league play.",
  },
  {
    id: "04",
    title: "Pro Pathway",
    age_group: "U18 – Senior",
    schedule: "Mon – Fri · 17:00 – 19:30",
    description:
      "Performance training, scout exposure and trial preparation for PSL clubs.",
  },
];

export default function Programs() {
  const [programs, setPrograms] = useState(FALLBACK);

  useEffect(() => {
    axios
      .get(`${API}/programs`)
      .then((r) => Array.isArray(r.data) && r.data.length && setPrograms(r.data))
      .catch(() => {});
  }, []);

  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative py-24 lg:py-36 px-6 lg:px-10 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 fade-up">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-red-600" />
              <span className="text-xs uppercase tracking-[0.3em] text-red-600">
                Training Programs
              </span>
            </div>
            <h2
              data-testid="programs-title"
              className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight max-w-3xl"
            >
              Pathways from <br />
              <span className="text-red-600">First Touch</span> to First Team.
            </h2>
          </div>
          <p className="max-w-md text-white/60 leading-relaxed">
            Every program is built around age-appropriate development phases.
            Pick the level that matches your player — we'll handle the rest.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {programs.map((p) => (
            <div
              key={p.id}
              data-testid={`program-card-${p.id}`}
              className="group relative border-r border-b border-white/10 p-8 lg:p-10 hover:bg-red-600 transition-colors duration-300 cursor-pointer"
            >
              <div className="flex items-start justify-between mb-12">
                <span className="font-heading text-red-600 group-hover:text-white text-5xl font-bold tracking-tight transition-colors">
                  {p.id}
                </span>
                <ArrowUpRight className="h-5 w-5 text-white/30 group-hover:text-white transition-colors" />
              </div>

              <h3 className="font-heading uppercase text-2xl lg:text-3xl tracking-tight leading-none mb-3 group-hover:text-white transition-colors">
                {p.title}
              </h3>

              <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-white/50 group-hover:text-white/80 mb-6 transition-colors">
                {p.age_group}
              </span>

              <p className="text-sm text-white/60 group-hover:text-white/90 leading-relaxed mb-8 transition-colors">
                {p.description}
              </p>

              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 group-hover:text-white transition-colors">
                <Clock className="h-3.5 w-3.5" />
                {p.schedule}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
